import React from 'react';
import { Snackbar, Alert, Box, styled } from '@mui/material';
import { trackMixpanelEvent } from 'utils/utilMethods';
import { MixpanelEventName } from 'utils/constants';
import SilenceDetectionSound from 'assets/sounds/SilenceDetectionSound.mp3';
import { playSound } from 'utils/appUtils';


const intervalForSilenceDetection = 10000;
const minHeight = 8;

// Styled Components for the Bars (using MUI's styled)
const VolumeBar = styled(Box)(({ theme, height }) => ({
  backgroundColor: theme.custom.colors.lightSuccessMain,
  width: minHeight,
  borderRadius: 4,
  height: `${Math.max(minHeight, +(height ?? 0))}px`,
  transition: 'height 0.1s ease-in-out',
}));

const VolumeIndicatorContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '80px',
  height: '40px',
});


interface VolumeIndicatorProps {
  volumeLevel: number;
  threshold: number;
  maxVolume?: number;
  barCount?: number;
}

function VolumeIndicator({
  volumeLevel,
  maxVolume = 80,
  barCount = 5,
  threshold,
}: VolumeIndicatorProps) {
  const [barHeights, setBarHeights] = React.useState<number[]>(Array(barCount).fill(0));

  React.useEffect(() => {
    const heights: number[] = [];
    const middleBarMultiplier = 1.75;
    const containerHeight = 40 / middleBarMultiplier;
    const normalizedVolume = Math.min(1, Math.max(0, volumeLevel / maxVolume));
    const baseHeight = normalizedVolume * containerHeight;

    for (let i = 0; i < barCount; i += 1) {
      let height = baseHeight;
      // Adjust height for odd and even bar counts
      if (i % 2 === 1) { // Odd number of bars
        height *= volumeLevel < threshold ? middleBarMultiplier * 0.75 : middleBarMultiplier;
      }
      else {
        height *= middleBarMultiplier * 0.75;
      }


      heights.push(height);
    }
    setBarHeights(heights);
  }, [volumeLevel, maxVolume, barCount, threshold]);

  return (
    <VolumeIndicatorContainer>
      {barHeights.map((height, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <VolumeBar key={index} height={height} sx={{ mx: '4px' }} />
      ))}
    </VolumeIndicatorContainer>
  );
}

interface SilenceDetectionProps {
  volumeLevel: number;
  threshold: number;
}

function SilenceDetection({ volumeLevel, threshold }: SilenceDetectionProps) {
  const [showWarning, setShowWarning] = React.useState(false);
  const silenceDurationRef = React.useRef<number>(0);

  React.useEffect(() => {
    if (volumeLevel < threshold) {
      silenceDurationRef.current += 20;
      if (silenceDurationRef.current >= intervalForSilenceDetection) {
        silenceDurationRef.current = 0;
        trackMixpanelEvent(MixpanelEventName.SESSION_SILENCE_DETECTED);
        playSound(SilenceDetectionSound);
        setShowWarning(true);
      }
    }
    else {
      silenceDurationRef.current = 0;
    }
  }, [volumeLevel, threshold]);

  const handleClose = () => {
    setShowWarning(false);
  };

  return (
    <>
      <Snackbar
        open={showWarning}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        autoHideDuration={6000}
        onClose={handleClose}
      >
        <Alert severity='warning' sx={{ width: '100%' }} onClose={handleClose}>
          We have detected silence. Are you still recording?
        </Alert>
      </Snackbar>
      <VolumeIndicator volumeLevel={volumeLevel} threshold={threshold} />
    </>
  );
}

export default SilenceDetection;
